import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    IconButton,
    Typography,
    Box,
    Avatar,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Snackbar,
    Alert,
    FormControl,
    Select,
    MenuItem
} from '@mui/material';
import {
    Close as CloseIcon,
    Receipt as InvoiceIcon,
    Person as PersonIcon,
    Event as EventIcon,
    Calculate as CalculateIcon
} from '@mui/icons-material';
import { updateData, postData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const RentInvoiceModal = ({
    open,
    onClose,
    deliveryData = null,
    mode = 'view',
    onSave = null
}) => {
    const { companyId, branchId, token, userId } = useAuth();
    const isViewMode = mode === 'view';
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showError, setShowError] = useState(false);
    const [invoiceData, setInvoiceData] = useState({
        // Bill Info
        BillNo: '',
        BillNumber: '',
        BillPrefix: '',
        FinYear: '',
        BillDate: '',

        // Customer Info
        CustomerName: '',
        ContactNumber1: '',
        ContactNumber2: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',

        // Event Info
        EventName: '',
        EventLocation: '',
        EventAddress: '',

        // Dates
        DeliveryOutDate: '',
        DeliveryReturnDate: '',
        DeliveryInCreatedDate: '',
        EventDays: 0,
        RentDays: 0,

        // Items
        items: [],

        // Amounts
        Amount: 0,
        DiscountAmount: 0,
        NetAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        RoundOff: 0,
        TransportingCharge: 0,
        AdditionalAmount: 0,
        TotalAmount: 0,
        AdvanceAmount: 0,
        PaidAmount: 0,
        BalanceAmount: 0,
        DamagedQuantity: 0,

        // GST Setting
        IsGST: true,

        // Payment Mode
        ModeOfPayment: 1,

        // Remarks
        AdditionalRemarks: ''
    });
    const [hasChanges, setHasChanges] = useState(false);
    const [originalData, setOriginalData] = useState(null);
    const [showDamageAlert, setShowDamageAlert] = useState(false);

    useEffect(() => {
        if (open && deliveryData) {
            // Fetch bill number and branch IsGST first, then process invoice data
            if (mode !== 'view') {
                initializeInvoice();
            } else {
                processInvoiceData();
            }
        }
        // Reset changes tracking when modal opens
        setHasChanges(false);
    }, [open, deliveryData]);

    // Check for damage quantity and show alert
    useEffect(() => {
        if (open && invoiceData.items && invoiceData.items.length > 0) {
            const totalDamageQty = invoiceData.items.reduce((sum, item) => sum + (parseFloat(item.DamagedQuantity) || 0), 0);
            if (totalDamageQty > 0 && invoiceData.AdditionalAmount === 0) {
                setShowDamageAlert(true);
            } else {
                setShowDamageAlert(false);
            }
        }
    }, [open, invoiceData.items, invoiceData.AdditionalAmount]);

    // Track changes in view mode
    useEffect(() => {
        if (mode === 'view' && originalData && invoiceData) {
            const hasChanged = JSON.stringify(invoiceData) !== JSON.stringify(originalData);
            setHasChanges(hasChanged);
        }
    }, [invoiceData, originalData, mode]);

    // Initialize invoice by fetching bill number and branch IsGST
    const initializeInvoice = async () => {
        try {
            // Check if this is an existing invoice (view/edit mode)
            const isExistingInvoice = deliveryData.RentInvoiceDetails || (deliveryData.RentInvoice && deliveryData.RentInvoiceDetails);
            
            if (isExistingInvoice) {
                // For existing invoices, just process the data without fetching bill number
                const invoice = deliveryData.RentInvoice || deliveryData;
                console.log('Existing invoice detected, processing with IsGST:', invoice.IsGST);
                processInvoiceData(invoice.IsGST ?? true);
                return;
            }

            // For new invoices, fetch bill number and branch IsGST in parallel
            const [billResponse, branchResponse] = await Promise.all([
                fetchData('RentInvoiceApi', `/MaxBillno/${companyId}/${branchId}`, token),
                fetchData('BranchApi', `/BranchDetails/${branchId}`, token)
            ]);

            console.log('Bill number response:', billResponse);
            console.log('Branch response:', branchResponse);

            // Process bill number
            const billData = billResponse.response_data || billResponse || {};
            const billNumber = billData.BillNumber || billData.billNumber || 1;
            const billPrefix = billData.BillPrefix || billData.billPrefix || '';
            const finYear = billData.FinYear || billData.finYear || '';
            const billNo = `${billPrefix}/${String(billNumber)}/${finYear}`;

            // Process branch IsGST
            const branchData = branchResponse.response_data || branchResponse.data || branchResponse;
            const isGST = branchData.IsGST ?? true; // Default to true if not specified

            console.log('Branch IsGST value:', isGST);
            console.log('Generated BillNo:', billNo);

            // Update state with bill number and IsGST
            setInvoiceData(prev => ({
                ...prev,
                BillNumber: billNumber,
                BillPrefix: billPrefix,
                BillNo: billNo,
                FinYear: finYear,
                BillDate: new Date().toISOString().split('T')[0],
                IsGST: isGST
            }));

            // Now process invoice data with the correct IsGST value
            processInvoiceData(isGST);
        } catch (error) {
            console.error('Error initializing invoice:', error);
            // Fallback to default values
            processInvoiceData(true);
        }
    };

    const calculateDaysDifference = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const processInvoiceData = (isGST = true) => {
        setLoading(true);

        // Check if this is an existing invoice (view/edit mode) or new invoice (create mode)
        const isExistingInvoice = deliveryData.RentInvoiceDetails || (deliveryData.RentInvoice && deliveryData.RentInvoiceDetails);

        if (isExistingInvoice) {
            // Handle existing invoice data
            // Data can be in two formats:
            // 1. { RentInvoice: {...}, RentInvoiceDetails: [...] }
            // 2. { ...invoiceFields, RentInvoiceDetails: [...] } (flattened)
            const invoice = deliveryData.RentInvoice || deliveryData;
            const details = deliveryData.RentInvoiceDetails || [];

            console.log('Processing existing invoice:', invoice);
            console.log('Invoice details:', details);

            // Process items from RentInvoiceDetails
            const items = details.map(item => ({
                id: item.Id || 0,
                ItemId: item.ItemId,
                ItemName: item.ItemName,
                HSNCode: item.HSNCode,
                Quantity: parseFloat(item.Quantity) || 0,
                DamagedQuantity: parseFloat(item.DamagedQuantity) || 0,
                Rate: parseFloat(item.Rate) || 0,
                Days: parseFloat(item.Days) || parseFloat(invoice.RentDays) || 0,
                Amount: parseFloat(item.Amount) || 0,
                DiscountPercentage: parseFloat(item.DiscountPercentage) || 0,
                DiscountAmount: parseFloat(item.DiscountAmount) || 0,
                TaxableAmount: parseFloat(item.TaxableAmount) || 0,
                GSTRate: parseFloat(item.GSTRate) || 0,
                CGSTAmount: parseFloat(item.CGSTAmount) || 0,
                SGSTAmount: parseFloat(item.SGSTAmount) || 0,
                IGSTAmount: parseFloat(item.IGSTAmount) || 0,
                TotalAmount: parseFloat(item.TotalAmount) || 0
            }));

            setInvoiceData(prev => {
                const newData = {
                    ...prev,
                    // Bill Info
                    Id: invoice.Id || 0,
                    BillNo: invoice.BillNo || `${invoice.BillPrefix}/${invoice.BillNumber}/${invoice.FinYear}`,
                    BillNumber: invoice.BillNumber || 0,
                    BillPrefix: invoice.BillPrefix || '',
                    FinYear: invoice.FinYear || '',
                    BillDate: invoice.BillDate ? invoice.BillDate.split('T')[0] : '',

                    // Customer Info
                    CustomerName: invoice.CustomerName || '',
                    ContactNumber1: invoice.ContactNumber1 || '',
                    ContactNumber2: invoice.ContactNumber2 || '',
                    AddressLine1: invoice.AddressLine1 || '',
                    AddressLine2: invoice.AddressLine2 || '',
                    Location: invoice.Location || '',
                    PinCode: invoice.PinCode || '',

                    // Event Info
                    EventName: invoice.EventName || '',
                    EventLocation: invoice.EventLocation || '',
                    EventAddress: invoice.EventAddress || '',

                    // Dates
                    DeliveryOutDate: invoice.DeliveryOutDate ? invoice.DeliveryOutDate.split('T')[0] : '',
                    DeliveryReturnDate: invoice.DeliveryReturnDate ? invoice.DeliveryReturnDate.split('T')[0] : '',
                    DeliveryInCreatedDate: invoice.DeliveryInCreatedDate ? invoice.DeliveryInCreatedDate.split('T')[0] : '',
                    EventDays: invoice.EventDays || 0,
                    RentDays: invoice.RentDays || 0,

                    // Items
                    items: items,

                    // Amounts
                    Amount: parseFloat(invoice.Amount) || 0,
                    DiscountAmount: parseFloat(invoice.DiscountAmount) || 0,
                    NetAmount: parseFloat(invoice.NetAmount) || 0,
                    CGSTAmount: parseFloat(invoice.CGSTAmount) || 0,
                    SGSTAmount: parseFloat(invoice.SGSTAmount) || 0,
                    IGSTAmount: parseFloat(invoice.IGSTAmount) || 0,
                    RoundOff: parseFloat(invoice.RoundOff) || 0,
                    TransportingCharge: parseFloat(invoice.TransportingCharge) || 0,
                    AdditionalAmount: parseFloat(invoice.AdditionalAmount) || 0,
                    TotalAmount: parseFloat(invoice.TotalAmount) || 0,
                    AdvanceAmount: parseFloat(invoice.AdvanceAmount) || 0,
                    PaidAmount: parseFloat(invoice.PaidAmount) || 0,
                    BalanceAmount: parseFloat(invoice.BalanceAmount) || 0,

                    // GST Setting
                    IsGST: invoice.IsGST ?? true,

                    // Payment Mode
                    ModeOfPayment: invoice.ModeOfPayment || 1,

                    // Remarks
                    AdditionalRemarks: invoice.Remarks || ''
                };

                console.log('Processed invoice data:', newData);

                // Store original data for comparison in view mode
                if (mode === 'view') {
                    setOriginalData(JSON.parse(JSON.stringify(newData)));
                }

                return newData;
            });

            setLoading(false);
            return;
        }

        // Handle new invoice from DeliveryCall (create mode)
        const rentDays = calculateDaysDifference(
            deliveryData.DeliveryDate,
            deliveryData.DeliveryInCreatedDate || deliveryData.DeliveryReturnDate
        );

        // Process items and calculate amounts
        const items = (deliveryData.DeliveryCallDetails || []).map(item => {
            const quantity = parseFloat(item.DeliveryInQuantity || item.DeliveryOutQuantity) || 0;
            const damageQuantity = parseFloat(item.DamagedQuantity) || 0;
            const rate = parseFloat(item.Rate) || 0;
            const gstRate = parseFloat(item.GSTRate) || 0;

            // Calculate per item
            const amount = quantity * rate * rentDays;
            const discountPercentage = 0; // Can be added later
            const discountAmount = (amount * discountPercentage) / 100;
            const taxableAmount = amount - discountAmount;

            // GST Calculation (assuming CGST + SGST for intra-state)
            const cgstAmount = isGST ? (taxableAmount * gstRate) / 200 : 0; // Half of GST rate
            const sgstAmount = isGST ? (taxableAmount * gstRate) / 200 : 0; // Half of GST rate
            const igstAmount = 0; // For inter-state, would be full GST rate

            const totalAmount = taxableAmount + cgstAmount + sgstAmount + igstAmount;

            return {
                ItemId: item.ItemId,
                ItemName: item.ItemName,
                HSNCode: item.HSNCode,
                Quantity: quantity,
                DamagedQuantity: damageQuantity,
                Rate: rate,
                Days: rentDays,
                Amount: amount,
                DiscountPercentage: discountPercentage,
                DiscountAmount: discountAmount,
                TaxableAmount: taxableAmount,
                GSTRate: gstRate,
                CGSTAmount: cgstAmount,
                SGSTAmount: sgstAmount,
                IGSTAmount: igstAmount,
                TotalAmount: totalAmount
            };
        });

        // Calculate totals
        const amount = items.reduce((sum, item) => sum + item.Amount, 0);
        const discountAmount = items.reduce((sum, item) => sum + item.DiscountAmount, 0);
        const netAmount = amount - discountAmount;
        const cgstAmount = items.reduce((sum, item) => sum + item.CGSTAmount, 0);
        const sgstAmount = items.reduce((sum, item) => sum + item.SGSTAmount, 0);
        const igstAmount = items.reduce((sum, item) => sum + item.IGSTAmount, 0);
        
        const transportingCharge = 0; // Can be added later
        const additionalAmount = 0; // Can be added later
        const subtotal = netAmount + cgstAmount + sgstAmount + igstAmount + transportingCharge + additionalAmount;

        // Round off calculation
        const roundOff = Math.round(subtotal) - subtotal;
        const totalAmount = Math.round(subtotal);

        const advanceAmount = parseFloat(deliveryData.AdvanceAmount) || 0; // Load from delivery data
        const paidAmount = 0; // Can be fetched from payment records
        const balanceAmount = totalAmount - advanceAmount - paidAmount;

        setInvoiceData(prev => {
            const newData = {
                ...prev,
                CustomerName: deliveryData.CustomerName || '',
                ContactNumber1: deliveryData.ContactNumber1 || '',
                ContactNumber2: deliveryData.ContactNumber2 || '',
                AddressLine1: deliveryData.AddressLine1 || '',
                AddressLine2: deliveryData.AddressLine2 || '',
                Location: deliveryData.Location || '',
                PinCode: deliveryData.PinCode || '',

                EventName: deliveryData.EventName || '',
                EventLocation: deliveryData.EventLocation || '',
                EventAddress: deliveryData.EventAddress || '',

                DeliveryOutDate: deliveryData.DeliveryDate || '',
                DeliveryReturnDate: deliveryData.DeliveryReturnDate || '',
                DeliveryInCreatedDate: deliveryData.DeliveryInCreatedDate || '',
                EventDays: deliveryData.EventDays || 0,
                RentDays: rentDays,

                items: items,

                Amount: amount,
                DiscountAmount: discountAmount,
                NetAmount: netAmount,
                CGSTAmount: cgstAmount,
                SGSTAmount: sgstAmount,
                IGSTAmount: igstAmount,
                RoundOff: roundOff,
                TransportingCharge: transportingCharge,
                AdditionalAmount: additionalAmount,
                TotalAmount: totalAmount,
                AdvanceAmount: advanceAmount,
                PaidAmount: paidAmount,
                BalanceAmount: balanceAmount,
                IsGST: isGST,
                AdditionalRemarks: ''
            };

            // Store original data for comparison in view mode
            if (mode === 'view') {
                setOriginalData(JSON.parse(JSON.stringify(newData)));
            }

            return newData;
        });

        setLoading(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const handleIsGSTChange = (newIsGST) => {
        setInvoiceData(prev => {
            // Recalculate all items with new GST setting
            const updatedItems = prev.items.map(item => {
                const quantity = parseFloat(item.Quantity) || 0;
                const rate = parseFloat(item.Rate) || 0;
                const days = parseFloat(item.Days) || 0;
                const gstRate = newIsGST ? (parseFloat(item.GSTRate) || 0) : 0;
                const discountPct = parseFloat(item.DiscountPercentage) || 0;

                const amount = quantity * rate * days;
                const discountAmount = (amount * discountPct) / 100;
                const taxableAmount = amount - discountAmount;
                const gstAmount = newIsGST ? (taxableAmount * gstRate) / 100 : 0;

                return {
                    ...item,
                    CGSTAmount: newIsGST ? Math.round((gstAmount / 2) * 100) / 100 : 0,
                    SGSTAmount: newIsGST ? Math.round((gstAmount / 2) * 100) / 100 : 0,
                    IGSTAmount: 0,
                    TotalAmount: Math.round((taxableAmount + (newIsGST ? gstAmount : 0)) * 100) / 100
                };
            });

            // Recalculate totals
            const amount = updatedItems.reduce((sum, item) => sum + item.Amount, 0);
            const discountAmount = updatedItems.reduce((sum, item) => sum + item.DiscountAmount, 0);
            const netAmount = amount - discountAmount;
            const cgstAmount = updatedItems.reduce((sum, item) => sum + item.CGSTAmount, 0);
            const sgstAmount = updatedItems.reduce((sum, item) => sum + item.SGSTAmount, 0);
            const igstAmount = updatedItems.reduce((sum, item) => sum + item.IGSTAmount, 0);
            
            const subtotal = netAmount + cgstAmount + sgstAmount + igstAmount + prev.TransportingCharge + prev.AdditionalAmount;
            const roundOff = Math.round(subtotal) - subtotal;
            const totalAmount = Math.round(subtotal);
            const balanceAmount = totalAmount - prev.AdvanceAmount - prev.PaidAmount;

            return {
                ...prev,
                IsGST: newIsGST,
                items: updatedItems,
                CGSTAmount: cgstAmount,
                SGSTAmount: sgstAmount,
                IGSTAmount: igstAmount,
                RoundOff: roundOff,
                TotalAmount: totalAmount,
                BalanceAmount: balanceAmount
            };
        });
    };

    const handleSaveInvoice = async () => {
        setSaving(true);
        try {
            // Check if this is an update (edit/view mode with existing invoice)
            const isUpdate = (mode === 'edit' || mode === 'view') && deliveryData.Id;
            
            // Prepare RentInvoiceDetails
            const rentInvoiceDetails = invoiceData.items.map(item => ({
                Id: item.Id || 0, // Include Id for updates, 0 for new items
                RentInvoiceId: deliveryData.Id || 0, // Link to parent invoice
                ItemId: item.ItemId,
                ItemName: item.ItemName,
                Quantity: item.Quantity,
                Rate: item.Rate,
                Days: item.Days,
                Amount: item.Amount,
                DiscountPercentage: item.DiscountPercentage,
                DiscountAmount: item.DiscountAmount,
                HSNCode: item.HSNCode,
                GSTRate: item.GSTRate,
                TaxableAmount: item.TaxableAmount,
                CGSTAmount: item.CGSTAmount,
                SGSTAmount: item.SGSTAmount,
                IGSTAmount: item.IGSTAmount,
                TotalAmount: item.TotalAmount,
                IsActive: true,
                CreatedBy: parseInt(userId),
                ModifiedBy: parseInt(userId),
                CreatedDate: item.CreatedDate || new Date().toISOString(),
                ModifiedDate: new Date().toISOString()
            }));

            // Prepare RentInvoice
            const rentInvoice = {
                Id: isUpdate ? deliveryData.Id : 0, // Include Id for updates
                CompanyId: parseInt(companyId),
                BranchId: parseInt(branchId),
                EnquiryId: deliveryData.EnquiryId || null,
                DeliveryCallId: deliveryData.DeliveryCallId || deliveryData.Id || null,
                CustomerId: deliveryData.CustomerId || null,
                CustomerName: invoiceData.CustomerName,
                ContactNumber1: invoiceData.ContactNumber1,
                ContactNumber2: invoiceData.ContactNumber2,
                AddressLine1: invoiceData.AddressLine1,
                AddressLine2: invoiceData.AddressLine2,
                Location: invoiceData.Location,
                PinCode: invoiceData.PinCode,
                BillNumber: invoiceData.BillNumber || 0,
                BillPrefix: invoiceData.BillPrefix || '',
                BillNo: invoiceData.BillNo || '',
                FinYear: invoiceData.FinYear || new Date().getFullYear().toString().slice(-2) + '-' + (new Date().getFullYear() + 1).toString().slice(-2),
                BillDate: invoiceData.BillDate || new Date().toISOString().split('T')[0],
                DeliveryOutDate: invoiceData.DeliveryOutDate || null,
                DeliveryReturnDate: invoiceData.DeliveryReturnDate || null,
                DeliveryInCreatedDate: invoiceData.DeliveryInCreatedDate || null,
                EventName: invoiceData.EventName || '',
                EventLocation: invoiceData.EventLocation || '',
                EventAddress: invoiceData.EventAddress || '',
                EventDays: invoiceData.EventDays || 0,
                RentDays: invoiceData.RentDays || 0,
                Amount: invoiceData.Amount,
                DiscountAmount: invoiceData.DiscountAmount,
                NetAmount: invoiceData.NetAmount,
                CGSTAmount: invoiceData.CGSTAmount,
                SGSTAmount: invoiceData.SGSTAmount,
                IGSTAmount: invoiceData.IGSTAmount,
                RoundOff: invoiceData.RoundOff,
                TransportingCharge: invoiceData.TransportingCharge,
                AdditionalAmount: invoiceData.AdditionalAmount,
                TotalAmount: invoiceData.TotalAmount,
                AdvanceAmount: invoiceData.AdvanceAmount,
                PaidAmount: invoiceData.PaidAmount,
                BalanceAmount: invoiceData.BalanceAmount,
                ModeOfPayment: parseInt(invoiceData.ModeOfPayment) || 1,
                AdditionalRemarks: invoiceData.AdditionalRemarks || `Rental for ${invoiceData.EventName} - ${invoiceData.RentDays} days`,
                IsActive: true,
                IsGST: invoiceData.IsGST,
                CreatedBy: isUpdate ? (deliveryData.CreatedBy || parseInt(userId)) : parseInt(userId),
                ModifiedBy: parseInt(userId),
                CreatedDate: isUpdate ? (deliveryData.CreatedDate || new Date().toISOString()) : new Date().toISOString(),
                ModifiedDate: new Date().toISOString()
            };

            const dataToSend = {
                RentInvoice: rentInvoice,
                RentInvoiceDetails: rentInvoiceDetails
            };

            console.log('=== Rent Invoice Data to Send ===');
            console.log('Mode:', mode);
            console.log('Is Update:', isUpdate);
            console.log('RentInvoice:', rentInvoice);
            console.log('RentInvoiceDetails:', rentInvoiceDetails);
            console.log('Full payload:', dataToSend);

            // Use appropriate API method based on mode
            let response;
            if (isUpdate) {
                response = await updateData('RentInvoiceApi', deliveryData.Id, dataToSend, token);
            } else {
                response = await postData('RentInvoiceApi', dataToSend, token);
            }

            console.log('API Response:', response);

            const isSuccess = response && (
                response.responseCode === '1111' ||
                response.responseCode === 1111 ||
                response.success === true
            );

            if (isSuccess) {
                setSuccessMessage(isUpdate ? 'Rent Invoice updated successfully!' : 'Rent Invoice saved successfully!');
                setShowSuccess(true);

                // Call onSave callback to refresh parent data
                if (onSave) {
                    onSave();
                }

                setTimeout(() => {
                    onClose();
                }, 1500);
            } else {
                setErrorMessage(isUpdate ? 'Failed to update invoice. Please try again.' : 'Failed to save invoice. Please try again.');
                setShowError(true);
            }
        } catch (error) {
            console.error('Error saving rent invoice:', error);
            setErrorMessage('Error saving invoice: ' + (error.message || 'Unknown error'));
            setShowError(true);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSuccess = () => {
        setShowSuccess(false);
    };

    const handleCloseError = () => {
        setShowError(false);
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '75vh',
                    width: '90vw',
                    maxWidth: '1200px'
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                p: 0.75
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 24, height: 24 }}>
                            <InvoiceIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                {mode === 'view' ? 'View Rent Invoice' : mode === 'edit' ? 'Update Rent Invoice' : 'Create Rent Invoice'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'white', p: 0.4 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0.75 }}>
                {loading && <LinearProgress sx={{ mb: 0.5 }} />}

                {/* Bill Number Display - Compact Design */}
                {!isViewMode && invoiceData.BillNo && (
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 0.5,
                            mt: 0.25,
                            mb: 0.5, 
                            background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                            border: '1.5px solid #10b981',
                            borderRadius: 1.5,
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Avatar 
                                    sx={{ 
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        width: 20,
                                        height: 20
                                    }}
                                >
                                    <InvoiceIcon sx={{ fontSize: 12 }} />
                                </Avatar>
                                <Box>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '0.55rem', 
                                            color: '#065f46', 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        Invoice Number
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '0.85rem', 
                                            fontWeight: 800, 
                                            color: '#047857',
                                            letterSpacing: '0.3px',
                                            fontFamily: 'monospace',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {invoiceData.BillNo}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '0.55rem', 
                                            color: '#065f46', 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px',
                                            lineHeight: 1.2,
                                            mb: 0.5
                                        }}
                                    >
                                        GST
                                    </Typography>
                                    <Box 
                                        onClick={() => handleIsGSTChange(!invoiceData.IsGST)}
                                        sx={{ 
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: invoiceData.IsGST ? 'flex-end' : 'flex-start',
                                            width: '50px',
                                            height: '24px',
                                            bgcolor: invoiceData.IsGST ? '#10b981' : '#e5e7eb',
                                            borderRadius: '12px',
                                            padding: '2px',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            border: `2px solid ${invoiceData.IsGST ? '#059669' : '#9ca3af'}`,
                                            '&:hover': {
                                                opacity: 0.8
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '18px',
                                                height: '18px',
                                                bgcolor: 'white',
                                                borderRadius: '50%',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '0.5rem',
                                                    fontWeight: 800,
                                                    color: invoiceData.IsGST ? '#10b981' : '#6b7280',
                                                    lineHeight: 1
                                                }}
                                            >
                                                {invoiceData.IsGST ? 'ON' : 'OFF'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '0.55rem', 
                                            color: '#065f46', 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px',
                                            lineHeight: 1.2,
                                            mb: 0.5
                                        }}
                                    >
                                        Payment Mode
                                    </Typography>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <Select
                                            value={invoiceData.ModeOfPayment}
                                            onChange={(e) => setInvoiceData(prev => ({ ...prev, ModeOfPayment: e.target.value }))}
                                            disabled={mode === 'view'}
                                            sx={{
                                                fontSize: '0.65rem',
                                                height: '24px',
                                                borderRadius: '6px',
                                                bgcolor: 'white',
                                                fontWeight: 600,
                                                color: '#047857',
                                                '& .MuiSelect-select': {
                                                    py: 0.25,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#10b981',
                                                    borderWidth: '1.5px'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#059669'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#047857',
                                                    borderWidth: '2px'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        borderRadius: '8px',
                                                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                                        mt: 0.5,
                                                        '& .MuiMenuItem-root': {
                                                            fontSize: '0.7rem',
                                                            minHeight: '28px',
                                                            py: 0.5,
                                                            px: 1.5,
                                                            fontWeight: 500,
                                                            '&:hover': {
                                                                bgcolor: '#f0fdf4',
                                                                color: '#047857'
                                                            },
                                                            '&.Mui-selected': {
                                                                bgcolor: '#dcfce7',
                                                                color: '#047857',
                                                                fontWeight: 600,
                                                                '&:hover': {
                                                                    bgcolor: '#bbf7d0'
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value={1}>💵 Cash</MenuItem>
                                            <MenuItem value={2}>💳 Card</MenuItem>
                                            <MenuItem value={3}>📱 UPI</MenuItem>
                                            <MenuItem value={4}>🏦 Bank Transfer</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '0.55rem', 
                                            color: '#065f46', 
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        Bill Date
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            fontWeight: 700, 
                                            color: '#047857',
                                            fontFamily: 'monospace',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {formatDate(invoiceData.BillDate)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                )}

                <Grid container spacing={0.5}>
                    {/* Customer Information */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 0.5, border: '1px solid #e2e8f0', borderRadius: 2, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Avatar sx={{ bgcolor: '#667eea', mr: 0.5, width: 18, height: 18 }}>
                                    <PersonIcon sx={{ fontSize: 10 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.7rem' }}>
                                    Customer Information
                                </Typography>
                            </Box>

                            <Grid container spacing={0.5}>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '90px' }}>Customer Name:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {invoiceData.CustomerName || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '60px' }}>Contact:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {invoiceData.ContactNumber1 || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '60px' }}>Location:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {invoiceData.Location || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '60px' }}>Address:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {[invoiceData.AddressLine1, invoiceData.AddressLine2, invoiceData.PinCode]
                                                .filter(Boolean)
                                                .join(', ') || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Event Information */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 0.5, border: '1px solid #e2e8f0', borderRadius: 2, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Avatar sx={{ bgcolor: '#10b981', mr: 0.5, width: 18, height: 18 }}>
                                    <EventIcon sx={{ fontSize: 10 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.7rem' }}>
                                    Event Information
                                </Typography>
                            </Box>

                            <Grid container spacing={0.5}>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '90px' }}>Event Name:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {invoiceData.EventName || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '100px' }}>Event Location:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {invoiceData.EventLocation || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '95px' }}>Event Address:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {invoiceData.EventAddress || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Rental Period */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 0.5, border: '1px solid #e2e8f0', borderRadius: 2, mb: 0.5, bgcolor: '#fef3c7' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Avatar sx={{ bgcolor: '#f59e0b', mr: 0.5, width: 18, height: 18 }}>
                                    <CalculateIcon sx={{ fontSize: 10 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.7rem' }}>
                                    Rental Period
                                </Typography>
                            </Box>

                            <Grid container spacing={0.5}>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '110px' }}>Delivery Out Date:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {formatDate(invoiceData.DeliveryOutDate)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '105px' }}>Delivery In Date:</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {formatDate(invoiceData.DeliveryInCreatedDate || invoiceData.DeliveryReturnDate)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '125px' }}>Event Days (Planned):</Typography>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2d3748' }}>
                                            {invoiceData.EventDays || 0} days
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096', minWidth: '120px' }}>Actual Rental Days:</Typography>
                                        <TextField
                                            type="number"
                                            value={invoiceData.RentDays}
                                            onChange={(e) => {
                                                const days = parseFloat(e.target.value) || 0;
                                                setInvoiceData(prev => {
                                                    // Recalculate items with new days
                                                    const updatedItems = prev.items.map(item => {
                                                        const amount = item.Quantity * item.Rate * days;
                                                        const taxableAmount = amount - item.DiscountAmount;
                                                        const gstRate = prev.IsGST ? (item.GSTRate || 0) : 0;
                                                        const cgstAmount = prev.IsGST ? (taxableAmount * gstRate) / 200 : 0;
                                                        const sgstAmount = prev.IsGST ? (taxableAmount * gstRate) / 200 : 0;
                                                        const totalAmount = taxableAmount + cgstAmount + sgstAmount;

                                                        return {
                                                            ...item,
                                                            Days: days,
                                                            Amount: amount,
                                                            TaxableAmount: taxableAmount,
                                                            CGSTAmount: cgstAmount,
                                                            SGSTAmount: sgstAmount,
                                                            TotalAmount: totalAmount
                                                        };
                                                    });

                                                    // Recalculate totals
                                                    const amount = updatedItems.reduce((sum, item) => sum + item.Amount, 0);
                                                    const discountAmount = updatedItems.reduce((sum, item) => sum + item.DiscountAmount, 0);
                                                    const netAmount = amount - discountAmount;
                                                    const cgstAmount = updatedItems.reduce((sum, item) => sum + item.CGSTAmount, 0);
                                                    const sgstAmount = updatedItems.reduce((sum, item) => sum + item.SGSTAmount, 0);
                                                    const subtotal = netAmount + cgstAmount + sgstAmount + prev.TransportingCharge + prev.AdditionalAmount;
                                                    const roundOff = Math.round(subtotal) - subtotal;
                                                    const totalAmount = Math.round(subtotal);
                                                    const balanceAmount = totalAmount - prev.AdvanceAmount - prev.PaidAmount;

                                                    return {
                                                        ...prev,
                                                        RentDays: days,
                                                        items: updatedItems,
                                                        Amount: amount,
                                                        NetAmount: netAmount,
                                                        CGSTAmount: cgstAmount,
                                                        SGSTAmount: sgstAmount,
                                                        RoundOff: roundOff,
                                                        TotalAmount: totalAmount,
                                                        BalanceAmount: balanceAmount
                                                    };
                                                });
                                            }}
                                            disabled={mode === 'view'}
                                            inputProps={{
                                                step: "0.5",
                                                min: "0"
                                            }}
                                            size="small"
                                            sx={{
                                                width: '80px',
                                                '& .MuiInputBase-input': {
                                                    fontSize: '0.75rem',
                                                    textAlign: 'center',
                                                    py: 0.3,
                                                    fontWeight: 700,
                                                    color: '#f59e0b'
                                                },
                                                '& .MuiInputBase-root': { height: '24px' }
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '0.7rem', color: '#718096' }}>days</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Items Table */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 0.5, border: '1px solid #e2e8f0', borderRadius: 2, mb: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.7rem', mb: 0.5 }}>
                                Items
                            </Typography>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ 
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            '& .MuiTableCell-root': {
                                                color: 'white',
                                                borderBottom: 'none'
                                            }
                                        }}>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5 }}>Item Name</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'center' }}>HSN</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'center' }}>Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'center' }}>Damage Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'right' }}>Rate/Day</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'center' }}>Days</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'right' }}>Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'center' }}>GST%</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'right' }}>CGST</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'right' }}>SGST</TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 0.5, textAlign: 'right' }}>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoiceData.items.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5 }}>{item.ItemName}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'center' }}>{item.HSNCode || '-'}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'center' }}>{item.Quantity}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'center', color: item.DamagedQuantity > 0 ? '#ef4444' : '#6b7280', fontWeight: item.DamagedQuantity > 0 ? 600 : 400 }}>
                                                    {item.DamagedQuantity || 0}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'right' }}>{formatCurrency(item.Rate)}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'center' }}>{item.Days}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'right' }}>{formatCurrency(item.Amount)}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'center' }}>{item.GSTRate}%</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'right' }}>{formatCurrency(item.CGSTAmount)}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'right' }}>{formatCurrency(item.SGSTAmount)}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 0.5, textAlign: 'right', fontWeight: 600 }}>
                                                    {formatCurrency(item.TotalAmount)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Damage Alert */}
                    {showDamageAlert && (
                        <Grid item xs={12}>
                            <Alert 
                                severity="warning" 
                                sx={{ 
                                    py: 0.25, 
                                    px: 0.75,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    bgcolor: '#fff3cd',
                                    border: '1px solid #ffc107',
                                    minHeight: 'auto',
                                    alignItems: 'center',
                                    '& .MuiAlert-icon': {
                                        fontSize: '0.9rem',
                                        padding: 0,
                                        marginRight: '6px'
                                    },
                                    '& .MuiAlert-message': {
                                        padding: 0
                                    }
                                }}
                            >
                                Damage quantity exists! Please add the damage amount in "Additional Amount" field.
                            </Alert>
                        </Grid>
                    )}

                    {/* Calculation Summary */}
                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 0.5, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#ffffff' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.7rem', mb: 0.5, letterSpacing: '0.3px' }}>
                                Calculation Summary
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                {/* Subtotal */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>Subtotal</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                        ₹ {invoiceData.Amount.toFixed(0)}
                                    </Typography>
                                </Box>

                                {/* Discount */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>Discount</Typography>
                                    <TextField
                                        type="number"
                                        value={invoiceData.DiscountAmount}
                                        onChange={(e) => {
                                            const discount = parseFloat(e.target.value) || 0;
                                            setInvoiceData(prev => {
                                                const netAmount = prev.Amount - discount;
                                                const subtotal = netAmount + prev.CGSTAmount + prev.SGSTAmount + prev.TransportingCharge + prev.AdditionalAmount;
                                                const roundOff = Math.round(subtotal) - subtotal;
                                                const totalAmount = Math.round(subtotal);
                                                const balanceAmount = totalAmount - prev.AdvanceAmount - prev.PaidAmount;
                                                return {
                                                    ...prev,
                                                    DiscountAmount: discount,
                                                    NetAmount: netAmount,
                                                    RoundOff: roundOff,
                                                    TotalAmount: totalAmount,
                                                    BalanceAmount: balanceAmount
                                                };
                                            });
                                        }}
                                        disabled={mode === 'view'}
                                        size="small"
                                        sx={{
                                            width: '100%',
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.75rem',
                                                textAlign: 'center',
                                                py: 0.25,
                                                fontWeight: 700,
                                                color: '#0f172a'
                                            },
                                            '& .MuiInputBase-root': { 
                                                height: '24px',
                                                bgcolor: '#f8fafc'
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#cbd5e1'
                                            }
                                        }}
                                    />
                                </Box>

                                {/* CGST */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>CGST</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                        ₹ {invoiceData.CGSTAmount.toFixed(0)}
                                    </Typography>
                                </Box>

                                {/* SGST */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>SGST</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                        ₹ {invoiceData.SGSTAmount.toFixed(0)}
                                    </Typography>
                                </Box>

                                {/* IGST */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>IGST</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                        ₹ {invoiceData.IGSTAmount.toFixed(0)}
                                    </Typography>
                                </Box>

                                {/* Transport Charge */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>Transport Charge</Typography>
                                    <TextField
                                        type="number"
                                        value={invoiceData.TransportingCharge}
                                        onChange={(e) => {
                                            const charge = parseFloat(e.target.value) || 0;
                                            setInvoiceData(prev => {
                                                const subtotal = prev.NetAmount + prev.CGSTAmount + prev.SGSTAmount + charge + prev.AdditionalAmount;
                                                const roundOff = Math.round(subtotal) - subtotal;
                                                const totalAmount = Math.round(subtotal);
                                                const balanceAmount = totalAmount - prev.AdvanceAmount - prev.PaidAmount;
                                                return {
                                                    ...prev,
                                                    TransportingCharge: charge,
                                                    RoundOff: roundOff,
                                                    TotalAmount: totalAmount,
                                                    BalanceAmount: balanceAmount
                                                };
                                            });
                                        }}
                                        disabled={mode === 'view'}
                                        size="small"
                                        sx={{
                                            width: '100%',
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.75rem',
                                                textAlign: 'center',
                                                py: 0.25,
                                                fontWeight: 700,
                                                color: '#0f172a'
                                            },
                                            '& .MuiInputBase-root': { 
                                                height: '24px',
                                                bgcolor: '#f8fafc'
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#cbd5e1'
                                            }
                                        }}
                                    />
                                </Box>

                                {/* Additional Amount */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: showDamageAlert ? '#fff3cd' : '#ffffff', 
                                    border: showDamageAlert ? '1.5px solid #ffc107' : '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: showDamageAlert ? '0 2px 4px rgba(255, 193, 7, 0.2)' : '0 1px 2px rgba(0,0,0,0.03)',
                                    animation: showDamageAlert ? 'pulse 2s ease-in-out infinite' : 'none',
                                    '@keyframes pulse': {
                                        '0%, 100%': { transform: 'scale(1)' },
                                        '50%': { transform: 'scale(1.02)' }
                                    }
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: showDamageAlert ? '#856404' : '#64748b', mb: 0.25, fontWeight: showDamageAlert ? 700 : 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>
                                        {showDamageAlert ? '⚠️ Additional Amount' : 'Additional Amount'}
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={invoiceData.AdditionalAmount}
                                        onChange={(e) => {
                                            const additionalAmt = parseFloat(e.target.value) || 0;
                                            setInvoiceData(prev => {
                                                const subtotal = prev.NetAmount + prev.CGSTAmount + prev.SGSTAmount + prev.TransportingCharge + additionalAmt;
                                                const roundOff = Math.round(subtotal) - subtotal;
                                                const totalAmount = Math.round(subtotal);
                                                const balanceAmount = totalAmount - prev.AdvanceAmount - prev.PaidAmount;
                                                return {
                                                    ...prev,
                                                    AdditionalAmount: additionalAmt,
                                                    RoundOff: roundOff,
                                                    TotalAmount: totalAmount,
                                                    BalanceAmount: balanceAmount
                                                };
                                            });
                                        }}
                                        disabled={mode === 'view'}
                                        size="small"
                                        sx={{
                                            width: '100%',
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.75rem',
                                                textAlign: 'center',
                                                py: 0.25,
                                                fontWeight: 700,
                                                color: showDamageAlert ? '#856404' : '#0f172a'
                                            },
                                            '& .MuiInputBase-root': { 
                                                height: '24px',
                                                bgcolor: showDamageAlert ? '#fffbf0' : '#f8fafc'
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: showDamageAlert ? '#ffc107' : '#cbd5e1',
                                                borderWidth: showDamageAlert ? '1.5px' : '1px'
                                            }
                                        }}
                                    />
                                </Box>

                                {/* Round Off */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>Round Off</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                        ₹ {invoiceData.RoundOff.toFixed(0)}
                                    </Typography>
                                </Box>

                                {/* Total Amount */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    border: '1px solid #059669', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.15)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', mb: 0.25, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>Total Amount</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#ffffff', lineHeight: 1.2 }}>
                                        ₹ {invoiceData.TotalAmount.toFixed(0)}
                                    </Typography>
                                </Box>

                                {/* Advance Amount */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>Advance Amount</Typography>
                                    <TextField
                                        type="number"
                                        value={invoiceData.AdvanceAmount}
                                        onChange={(e) => {
                                            const advance = parseFloat(e.target.value) || 0;
                                            setInvoiceData(prev => ({
                                                ...prev,
                                                AdvanceAmount: advance,
                                                BalanceAmount: prev.TotalAmount - advance - prev.PaidAmount
                                            }));
                                        }}
                                        disabled={mode === 'view'}
                                        size="small"
                                        sx={{
                                            width: '100%',
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.9rem',
                                                textAlign: 'center',
                                                py: 0.5,
                                                fontWeight: 700,
                                                color: '#0f172a'
                                            },
                                            '& .MuiInputBase-root': { 
                                                height: '32px',
                                                bgcolor: '#f8fafc'
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#cbd5e1'
                                            }
                                        }}
                                    />
                                </Box>

                                {/* Total - Advance */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '140px',
                                    bgcolor: '#fef3c7', 
                                    border: '1.5px solid #fbbf24', 
                                    borderRadius: 1.5, 
                                    p: 1,
                                    textAlign: 'center',
                                    boxShadow: '0 2px 4px rgba(251, 191, 36, 0.15)'
                                }}>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#92400e', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total - Advance</Typography>
                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#92400e' }}>
                                        ₹ {(invoiceData.TotalAmount - invoiceData.AdvanceAmount).toFixed(0)}
                                    </Typography>
                                </Box>

                                {/* Paid Amount */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #cbd5e1', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>Paid Amount</Typography>
                                    <TextField
                                        type="number"
                                        value={invoiceData.PaidAmount}
                                        onChange={(e) => {
                                            const paid = parseFloat(e.target.value) || 0;
                                            setInvoiceData(prev => ({
                                                ...prev,
                                                PaidAmount: paid,
                                                BalanceAmount: prev.TotalAmount - prev.AdvanceAmount - paid
                                            }));
                                        }}
                                        disabled={mode === 'view'}
                                        size="small"
                                        sx={{
                                            width: '100%',
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.75rem',
                                                textAlign: 'center',
                                                py: 0.25,
                                                fontWeight: 700,
                                                color: '#0f172a'
                                            },
                                            '& .MuiInputBase-root': { 
                                                height: '24px',
                                                bgcolor: '#f8fafc'
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#cbd5e1'
                                            }
                                        }}
                                    />
                                </Box>

                                {/* Balance Amount */}
                                <Box sx={{ 
                                    flex: '1 1 auto', 
                                    minWidth: '110px',
                                    bgcolor: '#fee2e2', 
                                    border: '1px solid #ef4444', 
                                    borderRadius: 1, 
                                    p: 0.5,
                                    textAlign: 'center',
                                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#991b1b', mb: 0.25, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>Balance Amount</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#991b1b', lineHeight: 1.2 }}>
                                        ₹ {invoiceData.BalanceAmount.toFixed(0)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 0.5 }} />

                            {/* Remarks Section */}
                            <Box>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#2d3748', mb: 0.25 }}>Remarks</Typography>
                                <TextField
                                    multiline
                                    rows={2}
                                    value={invoiceData.AdditionalRemarks}
                                    onChange={(e) => {
                                        setInvoiceData(prev => ({
                                            ...prev,
                                            AdditionalRemarks: e.target.value
                                        }));
                                    }}
                                    disabled={mode === 'view'}
                                    placeholder="Enter any additional remarks or notes..."
                                    fullWidth
                                    size="small"
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            fontSize: '0.65rem',
                                            py: 0.25
                                        }
                                    }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 0.5, gap: 0.5 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={saving}
                    sx={{
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        py: 0.4,
                        px: 1.5,
                        borderColor: '#94a3b8',
                        color: '#64748b',
                        '&:hover': {
                            borderColor: '#64748b',
                            bgcolor: '#f8fafc'
                        }
                    }}
                >
                    {mode === 'view' ? 'Close' : 'Cancel'}
                </Button>
                
                {/* Show Save Invoice button only in create mode */}
                {mode === 'create' && (
                    <Button
                        onClick={handleSaveInvoice}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            py: 0.4,
                            px: 1.5,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                            }
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Invoice'}
                    </Button>
                )}

                {/* Show Update Invoice button in edit mode */}
                {mode === 'edit' && (
                    <Button
                        onClick={handleSaveInvoice}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            py: 0.4,
                            px: 1.5,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                            }
                        }}
                    >
                        {saving ? 'Updating...' : 'Update Invoice'}
                    </Button>
                )}

                {/* Show Update Invoice button only in view mode when there are changes */}
                {mode === 'view' && hasChanges && (
                    <Button
                        onClick={handleSaveInvoice}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            py: 0.4,
                            px: 1.5,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                            }
                        }}
                    >
                        {saving ? 'Updating...' : 'Update Invoice'}
                    </Button>
                )}

                {/* Show Print Invoice button only in view mode */}
                {mode === 'view' && (
                    <Button
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            py: 0.4,
                            px: 1.5,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                            }
                        }}
                    >
                        Print Invoice
                    </Button>
                )}
            </DialogActions>

            {/* Success Message Snackbar */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={1500}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSuccess}
                    severity="success"
                    sx={{
                        width: '100%',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Error Message Snackbar */}
            <Snackbar
                open={showError}
                autoHideDuration={3000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseError}
                    severity="error"
                    sx={{
                        width: '100%',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default RentInvoiceModal;
